import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./PatchworkPaywall.module.scss"

/**
 * PatchworkPaywall (`/patchwork`) — an original, self-contained EXPIRATION /
 * RENEWAL paywall for Private Photo Vault Premium.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/patchwork.md
 * The five prior expiration concepts are Deep Freeze (cold fear + freeze meter),
 * Unbroken (warm calendar flood), Ahead (forking road / future-self scrub),
 * Keystone (masonry arch drag) and Solari (split-flap lever board). This is none
 * of them, on every axis:
 *   - Layout:      a hand-stitched memory QUILT on a quilting hoop — a 3×4
 *                  patchwork of photo patches bound by a running-stitch border,
 *                  with a thread spool + a "kept through" readout. No prior
 *                  paywall used a textile / quilt / needlework object.
 *   - Interaction: PULL THE THREAD — drag the needle and a running stitch travels
 *                  the whole binding, sewing the quilt closed corner by corner.
 *                  (Priors used freeze-watch, calendar-flood, road-scrub,
 *                  drag-a-keystone, pull-a-lever — never run-a-stitch.)
 *   - Register:    handmade heirloom warmth — "keep it all held together." Not
 *                  fear, not calendar-pride, not hopeful-road, not load-bearing,
 *                  not mechanical.
 *   - Art:         warm quilting-room LIGHT + PHOTOGRAPHIC patches; brand blue =
 *                  the SEWN / bound thread, warm amber = the loose thread ends.
 *                  The photos are ALWAYS full colour — only the stitching changes
 *                  (deliberately NOT a grey→colour bloom).
 *   - Value frame: CONTINUITY + endowed progress + honest loss-aversion for
 *                  RENEWAL — a year of memories already sewn into one whole;
 *                  renew and every new photo keeps getting sewn in automatically.
 *
 * Honest & App Store compliant: renewal keeps real Premium features running
 * (automatic full-resolution backup, private/encrypted, organized on every
 * device). Nothing is unpicked and nothing is deleted on expiration — the copy
 * says so outright ("not a stitch comes loose", "every photo stays yours"). No
 * fake countdown, no fake scarcity, real savings arithmetic. Yearly is the
 * featured, default plan and carries the largest price; monthly is small and
 * secondary. The patch photos are labelled Unsplash demo memories.
 */

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

type PlanKey = "yearly" | "monthly"

// ── Quilt geometry (fixed, un-scaled px so pointer deltas map 1:1) ──────────
const QW = 288 // quilt box width, px
const QH = 372 // quilt box height, px
const INSET = 12 // binding inset from the box edge
const RAD = 16 // binding corner radius
const STITCHES = 46 // running-stitch ticks around the binding

// The rounded-rectangle binding path the needle travels.
const bindingPath = (() => {
  const x = INSET
  const y = INSET
  const w = QW - INSET * 2
  const h = QH - INSET * 2
  const r = RAD
  return [
    `M ${x + r},${y}`,
    `H ${x + w - r}`,
    `A ${r} ${r} 0 0 1 ${x + w},${y + r}`,
    `V ${y + h - r}`,
    `A ${r} ${r} 0 0 1 ${x + w - r},${y + h}`,
    `H ${x + r}`,
    `A ${r} ${r} 0 0 1 ${x},${y + h - r}`,
    `V ${y + r}`,
    `A ${r} ${r} 0 0 1 ${x + r},${y}`,
    "Z",
  ].join(" ")
})()

// Twelve patches — the everyday memories the quilt is sewn from. Photos are
// bundled Unsplash demo memories (reused from the app's memory-photo set).
const PATCHES = [
  { src: "/byheart/m01.jpg", tag: "First steps" },
  { src: "/byheart/m02.jpg", tag: "Sunday market" },
  { src: "/byheart/m03.jpg", tag: "The lake" },
  { src: "/byheart/m04.jpg", tag: "Turning six" },
  { src: "/byheart/m05.jpg", tag: "Road trip" },
  { src: "/byheart/m06.jpg", tag: "Grandma's" },
  { src: "/byheart/m07.jpg", tag: "Backyard" },
  { src: "/byheart/m08.jpg", tag: "The ridge" },
  { src: "/byheart/m09.jpg", tag: "Biscuit" },
  { src: "/byheart/m13.jpg", tag: "Bubbles" },
  { src: "/byheart/m16.jpg", tag: "Autumn walk" },
  { src: "/byheart/m18.jpg", tag: "Day one" },
] as const

interface Tick {
  x: number
  y: number
  a: number // tangent angle, deg
  t: number // fraction along the path (0..1)
}

const Shield = ({ size = 18 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path
      d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      d="M9.2 12.1l2 2 3.6-3.9"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Check = ({ size = 12 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const PatchworkPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  // "Kept through" date — a year ahead of today once the quilt is bound. Uses a
  // fixed anchor (no Date.now dependence) so the debug render is deterministic.
  const keptThrough = "Jul 2027"

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // ── The stitch state ──────────────────────────────────────────────────────
  // `p` (0→1) is how much of the binding is sewn. Pull the needle to run the
  // stitch around the quilt; at p≈1 the quilt is bound.
  const [p, setP] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [grabbed, setGrabbed] = useState(false)
  const pRef = useRef(0)
  const lastPt = useRef<{ x: number; y: number } | null>(null)
  const rafRef = useRef<number | undefined>(undefined)

  // Binding path measurement → needle position + running-stitch ticks.
  const pathRef = useRef<SVGPathElement | null>(null)
  const [len, setLen] = useState(0)
  const [ticks, setTicks] = useState<Tick[]>([])

  useLayoutEffect(() => {
    const path = pathRef.current
    if (!path) return
    const L = path.getTotalLength()
    setLen(L)
    const out: Tick[] = []
    for (let i = 0; i < STITCHES; i++) {
      const t = (i + 0.5) / STITCHES
      const a = path.getPointAtLength(L * t)
      const b = path.getPointAtLength((L * t + 3) % L)
      out.push({
        x: a.x,
        y: a.y,
        a: (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI,
        t,
      })
    }
    setTicks(out)
  }, [])

  const setPClamped = useCallback((v: number) => {
    const c = clamp(v, 0, 1)
    pRef.current = c
    setP(c)
  }, [])

  const tweenTo = useCallback(
    (target: number, dur = 1100) => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      const from = pRef.current
      let start: number | null = null
      const step = (ts: number) => {
        if (start == null) start = ts
        const t = clamp((ts - start) / dur, 0, 1)
        const eased = 1 - Math.pow(1 - t, 3) // ease-out
        setPClamped(from + (target - from) * eased)
        if (t < 1) rafRef.current = requestAnimationFrame(step)
        else rafRef.current = undefined
      }
      rafRef.current = requestAnimationFrame(step)
    },
    [setPClamped],
  )

  // Auto-demo: run the stitch a third of the way and let it fall back, so the
  // pull gesture is obvious. Reduced motion → bind it fully and quietly.
  useEffect(() => {
    if (reduceMotion) {
      setPClamped(1)
      setGrabbed(true)
      return
    }
    const t1 = window.setTimeout(() => {
      if (pRef.current === 0 && !grabbed) tweenTo(0.34, 900)
    }, 700)
    const t2 = window.setTimeout(() => {
      if (pRef.current > 0 && pRef.current < 0.5 && !dragging) tweenTo(0, 700)
    }, 1900)
    return () => {
      window.clearTimeout(t1)
      window.clearTimeout(t2)
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  // Needle drag: project pointer movement onto the local path tangent so pulling
  // "along the seam" runs the stitch forward (and back).
  const tangentAt = useCallback(
    (frac: number) => {
      if (!ticks.length) return { tx: 1, ty: 0 }
      const idx = clamp(Math.round(frac * (STITCHES - 1)), 0, STITCHES - 1)
      const rad = (ticks[idx].a * Math.PI) / 180
      return { tx: Math.cos(rad), ty: Math.sin(rad) }
    },
    [ticks],
  )

  const onDown = useCallback((e: React.PointerEvent) => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    setGrabbed(true)
    setDragging(true)
    lastPt.current = { x: e.clientX, y: e.clientY }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      /* no-op */
    }
  }, [])

  const onMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging || !lastPt.current || !len) return
      const dx = e.clientX - lastPt.current.x
      const dy = e.clientY - lastPt.current.y
      lastPt.current = { x: e.clientX, y: e.clientY }
      const { tx, ty } = tangentAt(pRef.current)
      const dArc = dx * tx + dy * ty // px along the seam
      setPClamped(pRef.current + dArc / len)
    },
    [dragging, len, tangentAt, setPClamped],
  )

  const onUp = useCallback(() => {
    if (!dragging) return
    setDragging(false)
    lastPt.current = null
    // Snap: past the halfway knot → bind fully; otherwise ease back to loose.
    if (pRef.current >= 0.5) tweenTo(1, 700)
    else tweenTo(0, 600)
  }, [dragging, tweenTo])

  const onKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        setGrabbed(true)
        tweenTo(pRef.current >= 0.98 ? 0 : 1, 900)
      }
    },
    [tweenTo],
  )

  const bound = p >= 0.995
  const sewn = Math.round(p * PATCHES.length)
  const pct = Math.round(p * 100)

  // Needle position along the binding.
  const needle = useMemo(() => {
    const path = pathRef.current
    if (!path || !len) return { x: QW - INSET - RAD, y: INSET, a: 0 }
    const a = path.getPointAtLength(len * clamp(p, 0, 1))
    const b = path.getPointAtLength((len * clamp(p, 0, 1) + 3) % len)
    return {
      x: a.x,
      y: a.y,
      a: (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI,
    }
  }, [p, len])

  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  return (
    <div className={styles.root}>
      <div className={styles.grain} aria-hidden="true" />

      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark}>
            <Shield size={15} />
          </span>
          <span className={styles.brandText}>
            Private Photo Vault<em>Premium</em>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <div className={styles.intro}>
        <span className={styles.eyebrow}>
          <span className={styles.eyebrowDot} /> Premium · up for renewal
        </span>
        <h1 className={styles.title}>
          <span
            className={`${styles.titleLine} ${bound ? styles.titleOut : styles.titleIn}`}
          >
            A whole year,
            <br />
            sewn into one.
          </span>
          <span
            className={`${styles.titleLine} ${styles.titleOverlay} ${bound ? styles.titleIn : styles.titleOut}`}
            aria-hidden={!bound}
          >
            Bound. Kept
            <br />
            in one piece.
          </span>
        </h1>
      </div>

      {/* live readout above the quilt */}
      <div className={styles.readout}>
        <span
          className={`${styles.statePill} ${bound ? styles.statePillOn : ""}`}
        >
          <span className={styles.stateDot} />
          {bound ? "Kept through " + keptThrough : "Auto-sewing paused"}
        </span>
        <span className={styles.count}>
          <strong>{sewn}</strong> / {PATCHES.length} patches bound
        </span>
      </div>

      {/* ── THE QUILT ─────────────────────────────────────────────────────── */}
      <div className={styles.hoop}>
        <div
          className={`${styles.quilt} ${bound ? styles.quiltBound : ""}`}
          role="img"
          aria-label={`A memory quilt, ${pct}% bound. ${
            bound ? "Fully stitched." : "Not yet stitched all the way round."
          }`}
        >
          {/* patches */}
          <div className={styles.patches}>
            {PATCHES.map((patch, i) => {
              const patchBound = p >= (i + 0.6) / PATCHES.length
              return (
                <figure
                  key={patch.src}
                  className={`${styles.patch} ${patchBound ? styles.patchBound : ""}`}
                  style={{ ["--i" as any]: i }}
                >
                  <img
                    src={patch.src}
                    alt=""
                    draggable={false}
                    loading="eager"
                  />
                  <span className={styles.patchSeam} aria-hidden="true" />
                  <span className={styles.patchTack} aria-hidden="true" />
                  <figcaption className={styles.patchTag}>
                    {patch.tag}
                  </figcaption>
                </figure>
              )
            })}
          </div>

          {/* running-stitch binding + needle */}
          <svg
            className={styles.stitchLayer}
            viewBox={`0 0 ${QW} ${QH}`}
            aria-hidden="true"
          >
            {/* measured, invisible reference path */}
            <path ref={pathRef} d={bindingPath} fill="none" stroke="none" />
            {/* faint guide showing where the stitch will run */}
            <path
              className={styles.guide}
              d={bindingPath}
              fill="none"
              vectorEffect="non-scaling-stroke"
            />
            {/* the sewn running stitch — ticks pop in as the needle passes */}
            {ticks.map((tk, i) => (
              <rect
                key={i}
                className={`${styles.stitch} ${p >= tk.t ? styles.stitchOn : ""}`}
                x={-6}
                y={-1.6}
                width={12}
                height={3.2}
                rx={1.6}
                transform={`translate(${tk.x} ${tk.y}) rotate(${tk.a})`}
              />
            ))}
            {/* corner knots that snug in when the binding closes */}
            <circle
              className={`${styles.knot} ${bound ? styles.knotOn : ""}`}
              cx={INSET}
              cy={INSET}
              r={4}
            />
          </svg>

          {/* the draggable needle + trailing thread */}
          {len > 0 && (
            <button
              type="button"
              className={`${styles.needle} ${dragging ? styles.needleDrag : ""} ${
                !grabbed ? styles.needleIdle : ""
              } ${bound ? styles.needleDone : ""}`}
              style={{
                left: `${needle.x}px`,
                top: `${needle.y}px`,
                transform: `translate(-50%, -50%) rotate(${needle.a + 90}deg)`,
              }}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
              onKeyDown={onKey}
              aria-label="Pull the thread to bind your quilt and keep your memories stitched together"
            >
              <span className={styles.needleEye} aria-hidden="true" />
              <span className={styles.needleTip} aria-hidden="true" />
            </button>
          )}
        </div>

        <span
          className={`${styles.hint} ${bound ? styles.hintDone : ""}`}
          aria-live="polite"
        >
          {bound
            ? "Knotted tight — the whole quilt is bound"
            : p >= 0.5
              ? "Let go to knot it"
              : "Pull the thread to bind your quilt"}
        </span>
      </div>

      {/* honest renewal framing — no data-loss claim */}
      <p className={`${styles.reveal} ${bound ? styles.revealOn : ""}`}>
        {bound ? (
          <>
            Whole again for the year ahead — every new photo sewn in
            automatically, in full quality,{" "}
            <strong>kept private and only for you</strong>.
          </>
        ) : (
          <>
            All year, Premium quietly stitched each new photo into your vault.
            Let it lapse and <strong>not a stitch comes loose</strong> — every
            photo stays yours — but the automatic sewing pauses.
          </>
        )}
      </p>

      {/* what stays sewn in — real Premium features, not a perk grid */}
      <ul className={`${styles.holds} ${bound ? styles.holdsOn : ""}`}>
        <li style={{ ["--d" as any]: 0 }}>
          <span className={styles.holdMark}>
            <Check />
          </span>
          Every new photo &amp; video sewn in automatically — full resolution
        </li>
        <li style={{ ["--d" as any]: 1 }}>
          <span className={styles.holdMark}>
            <Check />
          </span>
          The whole quilt stays private &amp; encrypted — it opens only for you
        </li>
        <li style={{ ["--d" as any]: 2 }}>
          <span className={styles.holdMark}>
            <Check />
          </span>
          Arranged and in sync on every device you carry
        </li>
      </ul>

      {/* ── PRICING: yearly featured ─────────────────────────────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${plan === "yearly" ? styles.selected : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlySpool} aria-hidden="true">
            <span className={styles.spoolThread} />
          </span>
          <span className={styles.yearlyBody}>
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>Keep it whole · Yearly</span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
            </span>
            <span className={styles.yearlyPerMo}>
              about {yearlyPerMonth}/mo — sews in all twelve months
            </span>
          </span>
          <span className={styles.radio} aria-hidden="true" />
        </button>

        <button
          className={`${styles.monthly} ${plan === "monthly" ? styles.monthlySel : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyLeft}>
            <span className={styles.monthlyName}>Monthly</span>
            <span className={styles.monthlyNote}>one month at a time</span>
          </span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <p className={styles.assure}>
        Your photos are always yours — Premium keeps the automatic backup
        running, it never holds your library hostage.
      </p>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <p className={styles.credit}>Demo memories from Unsplash</p>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? hasTrial
                ? "Start free — keep it stitched"
                : "Keep it all stitched together"
              : "Continue monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? hasTrial
                ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
                : `${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default PatchworkPaywall
